import { TextareaHTMLAttributes, forwardRef, useState, useEffect, useRef } from 'react';
import { cn } from '@/utils/cn';

/**
 * Textarea Component
 *
 * Multi-line text input with auto-resize and character counter
 */

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  showCharCount?: boolean;
  autoResize?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      helperText,
      maxLength,
      showCharCount = false,
      autoResize = false,
      className = '',
      onChange,
      value,
      ...props
    },
    ref
  ) => {
    const [charCount, setCharCount] = useState(0);
    const internalRef = useRef<HTMLTextAreaElement | null>(null);

    // Auto-resize functionality
    useEffect(() => {
      if (autoResize && internalRef.current) {
        const textarea = internalRef.current;
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    }, [value, autoResize]);

    // Update character count
    useEffect(() => {
      if (value !== undefined) {
        setCharCount(String(value).length);
      }
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setCharCount(e.target.value.length);
      if (onChange) {
        onChange(e);
      }
    };

    return (
      <div className="w-full">
        {label && (
          <label
            className="block text-sm font-medium text-gray-700 mb-1"
            htmlFor={props.id}
          >
            {label}
          </label>
        )}
        <div className="relative">
          <textarea
            ref={(element) => {
              internalRef.current = element;
              if (typeof ref === 'function') {
                ref(element);
              } else if (ref) {
                ref.current = element;
              }
            }}
            value={value}
            onChange={handleChange}
            maxLength={maxLength}
            className={cn(
              'w-full px-4 py-3',
              'border border-gray-200 rounded-lg',
              'text-[16px] lg:text-sm text-gray-700 placeholder-gray-400',
              'focus:outline-none focus:border-gray-400',
              'transition-colors duration-150',
              'disabled:bg-gray-50 disabled:cursor-not-allowed',
              'resize-none',
              error ? 'border-danger' : '',
              className
            )}
            {...props}
          />
          {showCharCount && maxLength && (
            <div className="absolute bottom-2 right-2 text-xs text-gray-400">
              {charCount}/{maxLength}
            </div>
          )}
        </div>
        {helperText && !error && (
          <p className="mt-1 text-xs text-gray-500">{helperText}</p>
        )}
        {error && (
          <p className="mt-1 text-xs text-danger" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
